import React from 'react';

// Utils
import {
  getDetailCustomFieldValue,
  isFieldForCategory,
  isFieldForListingType,
  pickCategoryFields,
  pickCustomFieldProps,
} from '../../util/fieldHelpers.js';

import CustomExtendedDataSection from '../../components/CustomExtendedDataSection/CustomExtendedDataSection.js';

// Package weight is captured in EditListingDeliveryForm (publicData.shippingPackageWeightKg)
// for Skydropx shipping quotes. It's not a Console-managed listing field, so it's added here
// as a synthetic "detail" row rather than through the generic listingFieldConfigs pipeline.
const WEIGHT_FIELD_KEY = 'shippingPackageWeightKg';

// This field stores a plain number in the major currency unit (e.g. 1300 for $1,300.00).
// Unlike other 'long' fields, it represents money, so it needs currency formatting
// instead of the raw-number rendering that getDetailCustomFieldValue gives every other
// 'long' field. Cents are omitted here on purpose (formatMoney always shows them).
const REAL_PRICE_FIELD_KEY = 'precio_comparacion';

/**
 * Renders custom listing fields.
 * - SectionDetails is used if schemaType is 'enum', 'long', or 'boolean'
 * - SectionMultiEnum is used if schemaType is 'multi-enum'
 * - SectionText is used if schemaType is 'text'
 *
 * @param {*} props include publicData, metadata, listingFieldConfigs, categoryConfiguration
 * @returns React.Fragment containing aforementioned components
 */
const CustomListingFields = props => {
  const {
    publicData,
    metadata,
    listingFieldConfigs,
    categoryConfiguration,
    intl,
    currency,
  } = props;

  const { key: categoryPrefix, categories: listingCategoriesConfig } = categoryConfiguration;
  const categoriesObj = pickCategoryFields(publicData, categoryPrefix, 1, listingCategoriesConfig);
  const currentCategories = Object.values(categoriesObj);

  // Fields are shown by default. Set showConfig.displayOnListingPage to false to explicitly hide a field.
  const displayableFieldConfigs = listingFieldConfigs
    .filter(fieldConfig => fieldConfig.showConfig?.displayOnListingPage !== false)
    .concat(publicData[WEIGHT_FIELD_KEY] != null ? [{ key: WEIGHT_FIELD_KEY }] : []);

  const isFieldForSelectedCategories = fieldConfig => {
    const isTargetCategory = isFieldForCategory(currentCategories, fieldConfig);
    return isTargetCategory;
  };
  const propsForCustomFields =
    pickCustomFieldProps(
      { publicData, metadata },
      displayableFieldConfigs,
      'listingType',
      isFieldForSelectedCategories
    ) || [];

  const sectionDetailsProps = {
    ...props,
    isFieldForCategory: isFieldForSelectedCategories,
    fieldConfigs: displayableFieldConfigs,
    heading: 'ListingPage.detailsTitle',
  };

  const pickExtendedDataFields = (filteredConfigs, config) => {
    const { key } = config;

    if (key === WEIGHT_FIELD_KEY) {
      const weightKg = publicData[WEIGHT_FIELD_KEY];
      return typeof weightKg === 'undefined'
        ? filteredConfigs
        : filteredConfigs.concat({
            key: WEIGHT_FIELD_KEY,
            value: `${weightKg} kg`,
            label: intl.formatMessage({ id: 'CustomListingFields.weightLabel' }),
          });
    }

    const { schemaType, enumOptions, showConfig = {} } = config;
    const listingType = publicData.listingType;
    const isTargetListingType = isFieldForListingType(listingType, config);
    const isTargetCategory = isFieldForCategory(currentCategories, config);

    const { isDetail, label } = showConfig;
    const publicDataValue = publicData[key];
    const metadataValue = metadata[key];
    const value = publicDataValue != null ? publicDataValue : metadataValue;

    if (isDetail && isTargetListingType && isTargetCategory && typeof value !== 'undefined') {
      if (key === REAL_PRICE_FIELD_KEY) {
        const numericValue = Number(value);
        if (Number.isNaN(numericValue)) {
          return filteredConfigs;
        }
        const formattedRealPrice = intl.formatNumber(numericValue, {
          style: 'currency',
          currency,
          currencyDisplay: 'symbol',
          useGrouping: true,
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        });
        return filteredConfigs.concat({ key, value: formattedRealPrice, label });
      }

      const detailValue = getDetailCustomFieldValue(
        enumOptions,
        value,
        schemaType,
        key,
        label,
        intl,
        'ListingPage'
      );

      return detailValue ? filteredConfigs.concat(detailValue) : filteredConfigs;
    }
    return filteredConfigs;
  };

  return (
    <CustomExtendedDataSection
      sectionDetailsProps={sectionDetailsProps}
      propsForCustomFields={propsForCustomFields}
      idPrefix="listingPage"
      pickExtendedDataFields={pickExtendedDataFields}
    />
  );
};

export default CustomListingFields;
